// Layer management utilities
import type { LayerAPI, TransformState } from "../types/stage-types.js";

/**
 * Creates a layer manager for coordinating multiple layers
 */
export const createLayerManager = () => {
  let layers: LayerAPI[] = [];
  let isDestroyed = false;

  /**
   * Add a layer to the manager
   * @param layer - Layer to add
   */
  const addLayer = (layer: LayerAPI): void => {
    if (isDestroyed) return;

    layers.push(layer);
  };

  /**
   * Remove a layer from the manager
   * @param layer - Layer to remove
   * @returns True if layer was found and removed
   */
  const removeLayer = (layer: LayerAPI): boolean => {
    const index = layers.indexOf(layer);
    if (index > -1) {
      layers.splice(index, 1);
      return true;
    }
    return false;
  };

  /**
   * Get all layers
   * @returns Readonly array of layers
   */
  const getLayers = (): readonly LayerAPI[] => [...layers];

  /**
   * Notify all layers of a transform change
   * @param transform - New transform state
   */
  const notifyTransformChange = (transform: TransformState): void => {
    if (isDestroyed) return;

    layers.forEach((layer) => {
      if (layer.setTransform) {
        layer.setTransform(transform);
      }
    });
  };

  /**
   * Set visibility for all layers
   * @param visible - Visibility state
   */
  const setLayersVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    layers.forEach((layer) => {
      if (layer.setVisible) {
        layer.setVisible(visible);
      }
    });
  };

  /**
   * Destroy all layers and clean up
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    // Destroy all layers
    layers.forEach((layer) => {
      try {
        layer.destroy();
      } catch (error) {
        console.warn("Error destroying layer:", error);
      }
    });

    layers.length = 0;
    isDestroyed = true;
  };

  return {
    addLayer,
    removeLayer,
    getLayers,
    notifyTransformChange,
    setLayersVisible,
    destroy,
  };
};

/**
 * Supported application modes
 */
export type AppMode = "edit" | "run" | "sketch" | "ds" | "cms";

/**
 * Mode manager for handling application modes
 */
export const createModeManager = (initialMode: AppMode = "edit") => {
  let currentMode: AppMode = initialMode;
  let listeners: Array<(mode: AppMode) => void> = [];

  /**
   * Get current mode
   */
  const getMode = (): AppMode => currentMode;

  /**
   * Set new mode
   * @param mode - New mode to set
   */
  const setMode = (mode: AppMode): void => {
    if (currentMode === mode) return;

    currentMode = mode;

    // Notify all listeners
    listeners.forEach((listener) => listener(currentMode));
  };

  /**
   * Toggle between edit and run modes
   */
  const toggleMode = (): void => {
    setMode(currentMode === "edit" ? "run" : "edit");
  };

  /**
   * Add a listener for mode changes
   * @param listener - Function to call when mode changes
   * @returns Unsubscribe function
   */
  const onModeChange = (listener: (mode: AppMode) => void): (() => void) => {
    listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  };

  /**
   * Apply mode-specific styling to an element
   * @param element - Element to apply mode to
   */
  const applyModeToElement = (element: HTMLElement): void => {
    element.setAttribute("data-mode", currentMode);
  };

  /**
   * Cleanup function
   */
  const destroy = (): void => {
    listeners.length = 0;
  };

  return {
    getMode,
    setMode,
    toggleMode,
    onModeChange,
    applyModeToElement,
    destroy,
  };
};
